import { AppButton } from "components/button/AppButton";
import { Dropdown, Input, Menu, Popconfirm, Select, Skeleton, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { openNotification } from "utils/notification";
import { hasPermission } from "utils/hasPermission";
import { useGetCampaigns, QUERY_KEY_CAMPAIGNS } from "../hooks/useGetCampaigns";
import { useDeleteCampaign } from "../hooks/useDeleteCampaign";
import { TMarketingCampaign } from "../types";
import { AddCampaign } from "./AddCampaign";
import { EditCampaign } from "./EditCampaign";
import { CampaignStatsModal } from "./CampaignStatsModal";

export const CampaignsTable = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [targetFilter, setTargetFilter] = useState<string | undefined>();
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [selected, setSelected] = useState<TMarketingCampaign | null>(null);

  const { token, userInfo } = useGetUserInfo();
  const canManage = hasPermission(userInfo, "Manage Campaigns");
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useGetCampaigns(
    page,
    search,
    activeFilter,
    targetFilter
  );
  const { mutate: remove, isLoading: deleting } = useDeleteCampaign();

  const copyText = async (text?: string | null) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      openNotification({
        title: "Copied",
        state: "success",
        description: "Link copied to clipboard",
        duration: 2,
      });
    } catch {
      openNotification({
        title: "Copy failed",
        state: "error",
        description: "Could not copy link",
        duration: 3,
      });
    }
  };

  const columns: ColumnsType<TMarketingCampaign> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <div>
          <div className="font-medium">{name}</div>
          <code className="text-xs text-gray-500">{record.code}</code>
        </div>
      ),
    },
    {
      title: "Target",
      dataIndex: "target_type",
      key: "target_type",
      width: 110,
      render: (t) => <Tag>{t}</Tag>,
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, r) => (
        <Tag color={r.is_currently_active ? "green" : "default"}>
          {r.is_currently_active ? "Active" : "Off"}
        </Tag>
      ),
    },
    {
      title: "Clicks",
      dataIndex: "clicks_count",
      key: "clicks",
      width: 80,
    },
    {
      title: "Signups",
      key: "signups",
      width: 120,
      render: (_, r) => (
        <span>
          {r.attributions_count ?? 0}
          <span className="text-xs text-gray-400 ml-1">
            (T:{r.talent_signups_count ?? 0} / R:{r.recruiter_signups_count ?? 0})
          </span>
        </span>
      ),
    },
    {
      title: "",
      key: "action",
      width: 48,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu>
              <Menu.Item
                key="stats"
                onClick={() => {
                  setSelected(record);
                  setStatsOpen(true);
                }}
              >
                <i className="ri-bar-chart-box-line"></i> Stats & signups
              </Menu.Item>
              <Menu.Item
                key="copy-short"
                onClick={() => copyText(record.tracking_url)}
              >
                <i className="ri-link"></i> Copy short link
              </Menu.Item>
              <Menu.Item
                key="copy-direct"
                onClick={() => copyText(record.signup_url)}
              >
                <i className="ri-external-link-line"></i> Copy signup link
              </Menu.Item>
              {canManage && (
                <Menu.Item
                  key="edit"
                  onClick={() => {
                    setSelected(record);
                    setEditOpen(true);
                  }}
                >
                  <i className="ri-edit-2-fill"></i> Edit
                </Menu.Item>
              )}
              {canManage && (
                <Menu.Item key="delete">
                  <Popconfirm
                    title="Delete this campaign?"
                    description="Clicks and attributions for this campaign will also be removed."
                    onConfirm={() =>
                      remove(
                        { token, id: record.id },
                        {
                          onSuccess: () => {
                            openNotification({
                              title: "Deleted",
                              state: "success",
                              description: "Campaign deleted",
                              duration: 3,
                            });
                            queryClient.invalidateQueries({
                              queryKey: [QUERY_KEY_CAMPAIGNS],
                            });
                          },
                          onError: () => {
                            openNotification({
                              title: "Error",
                              state: "error",
                              description: "Could not delete campaign",
                              duration: 4,
                            });
                          },
                        }
                      )
                    }
                    okButtonProps={{ loading: deleting }}
                  >
                    <span className="text-red-600">
                      <i className="ri-delete-bin-line"></i> Delete
                    </span>
                  </Popconfirm>
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <i className="ri-more-2-fill text-lg cursor-pointer"></i>
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      {addOpen && (
        <AddCampaign open={addOpen} handleClose={() => setAddOpen(false)} />
      )}
      {editOpen && selected && (
        <EditCampaign
          open={editOpen}
          campaign={selected}
          handleClose={() => {
            setEditOpen(false);
            setSelected(null);
          }}
        />
      )}
      {statsOpen && selected && (
        <CampaignStatsModal
          open={statsOpen}
          campaign={selected}
          handleClose={() => {
            setStatsOpen(false);
            setSelected(null);
          }}
        />
      )}

      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-end mb-4">
        <div>
          <h4 className="font-semibold text-lg text-primary">
            Marketing campaigns
          </h4>
          <p className="text-sm text-gray-600">
            Create tracking links for marketing. Measure clicks and talent /
            recruiter signups per link.
          </p>
        </div>
        {canManage && (
          <AppButton label="New campaign" handleClick={() => setAddOpen(true)} />
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input.Search
          placeholder="Search name or code"
          allowClear
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={(v) => {
            setSearch(v.trim());
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          allowClear
          placeholder="Status"
          className="min-w-[8rem]"
          value={activeFilter}
          onChange={(v) => {
            setActiveFilter(v);
            setPage(1);
          }}
          options={[
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
          ]}
        />
        <Select
          allowClear
          placeholder="Target"
          className="min-w-[9rem]"
          value={targetFilter}
          onChange={(v) => {
            setTargetFilter(v);
            setPage(1);
          }}
          options={[
            { value: "any", label: "Any" },
            { value: "talent", label: "Talent" },
            { value: "recruiter", label: "Recruiter" },
          ]}
        />
      </div>

      {isLoading ? (
        <Skeleton active />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.data ?? []}
          loading={isFetching}
          pagination={{
            current: data?.current_page ?? page,
            total: data?.total ?? 0,
            pageSize: data?.per_page ?? 15,
            onChange: setPage,
          }}
        />
      )}
    </div>
  );
};
